// import endOfDay from 'date-fns/endOfDay'
import isEmpty from 'lodash/isEmpty.js'
import isEqual from 'lodash/isEqual.js'
import {createSelector} from 'reselect'

import {isEmptyValue} from '../../common/utils.js'
import {formsSelector} from '../../store.js'
import {
  DATE_CREATED,
  DATE_DELIVER_BY,
  DATE_DROPSHIPPED,
  DATE_ORDERED,
  DATE_RANGE_FILTERS,
  DATE_RANGE_FILTER_OPTIONS,
  DATE_SHIPPED,
  DATE_SHIP_BY,
} from '../../common/constants/OrderListDateRangeFilters.js'
import {
  AWAITING_FULFILLMENT,
  DEFAULT_EXPAND_MODE,
  DEFAULT_PER_PAGE,
  DEFAULT_SORT,
  DEFAULT_STATUS,
  DROPSHIPMENT_REQUESTED,
  ORDER_ALLOCATION_STATII,
  ORDER_PLURAL_URI_COMPONENT,
  ORDER_SINGLE_URI_COMPONENT,
  ORDER_STATII,
  ORDER_STATUS_BY_SLUG,
  SHIPPED,
} from '../../common/constants/Orders.js'
import {TAG_FILTER_OR} from '../../common/constants/Tags.js'
import {
  ensureArray,
  ensureArrayOfNumbers,
  ensureBoolean,
  ensureTroolean,
} from '../../common/ensure.js'
import {stringifyURL} from '../../common/querystring.js'
import {
  orderTagsSelector,
  orderTagsSortedByNameSelector,
} from '../../data/orderTags.js'
import {
  useDropshippingSelector,
  useInventoryAllocationSelector,
  usePartialInventoryAllocationSelector,
} from '../../data/company.js'
import {
  allOrdersSelector,
  canUseOrdersSelector,
  getOrdersActions,
} from '../../data/orders.js'
import {canUseReturnOrdersSelector} from '../../redux/selectors/data/returnOrders.js'
import {cartsSelector} from '../../data/carts.js'
import {hasOrderManageRevisionPermissionSelector} from '../../data/me.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {orderDetailOrderNumberSelector} from '../OrderDetailPage/orderDetailSelectors.js'
import {DETAIL_MODE, LIST_MODE} from '../../common/constants/index.js'
import {toZonedTime} from '../../common/zonedTime.js'

export const ORDER_LIST = 'ORDER_LIST'

export function orderListFormSelector(state) {
  return formsSelector(state)[ORDER_LIST]
}

export function visibleOrderNumbersSelector(state) {
  const form = orderListFormSelector(state)
  const orders = allOrdersSelector(state)

  let visibleOrderNumbers =
    form?.visibleOrderNumbers || visibleOrderNumbersSelector.default
  const filteredList = visibleOrderNumbers.filter(
    (orderNumber) =>
      orders[orderNumber] && !orders[orderNumber]._hidden_instead_of_removed,
  )

  if (filteredList.length === visibleOrderNumbers.length) {
    return visibleOrderNumbers
  } else {
    return filteredList
  }
}
visibleOrderNumbersSelector.default = []

export const isLoadingSelector = createSelector(
  orderListFormSelector,
  ({isLoading} = {}) => isLoading || false,
)

export const countSelector = createSelector(
  orderListFormSelector,
  ({count} = {}) => count || 0,
)

export const expandModeSelector = createSelector(
  orderListFormSelector,
  ({expandMode} = {}) => expandMode || DEFAULT_EXPAND_MODE,
)

export const expandedOrderNumbersSelector = createSelector(
  orderListFormSelector,
  ({expandedOrderNumbers} = {}) =>
    expandedOrderNumbers || expandedOrderNumbersSelector.default,
)
expandedOrderNumbersSelector.default = []

export const isAllExpandedSelector = createSelector(
  visibleOrderNumbersSelector,
  expandedOrderNumbersSelector,
  (visibleOrderNumbers, expandedOrderNumbers) =>
    visibleOrderNumbers.length !== 0 &&
    visibleOrderNumbers.length === expandedOrderNumbers.length,
)

export const selectedOrderNumbersSelector = createSelector(
  orderListFormSelector,
  ({selectedOrderNumbers} = {}) =>
    selectedOrderNumbers || selectedOrderNumbersSelector.default,
)
selectedOrderNumbersSelector.default = []

export const allSelectedSelector = createSelector(
  visibleOrderNumbersSelector,
  selectedOrderNumbersSelector,
  (visibleOrderNumbers, selected) =>
    visibleOrderNumbers.length > 0 &&
    visibleOrderNumbers.length === selected.length,
)

export const indeterminateSelectedSelector = createSelector(
  allSelectedSelector,
  selectedOrderNumbersSelector,
  (allSelected, selectedOrderNumbers) =>
    !allSelected && selectedOrderNumbers.length > 0,
)

export const activeOrderNumbersSelector = createSelector(
  selectedOrderNumbersSelector,
  orderDetailOrderNumberSelector,
  (selectedOrderNumbers, orderDetailOrderNumber) =>
    orderDetailOrderNumber ? [orderDetailOrderNumber] : selectedOrderNumbers,
)

export const activeOrdersSelector = createSelector(
  activeOrderNumbersSelector,
  (state) => allOrdersSelector(state),
  (activeOrderNumbers, orders) =>
    activeOrderNumbers.reduce((prev, orderNumber) => {
      if (orders[orderNumber]) {
        prev.push(orders[orderNumber])
      }

      return prev
    }, []),
)

export const selectedOrdersSelector = createSelector(
  (state) => allOrdersSelector(state),
  selectedOrderNumbersSelector,
  (orders, orderNumbers) =>
    orderNumbers.map((orderNumber) => orders[orderNumber]),
)

export const selectedOrderActionsSelector = createSelector(
  selectedOrdersSelector,
  canUseReturnOrdersSelector,
  cartsSelector,
  useInventoryAllocationSelector,
  hasOrderManageRevisionPermissionSelector,
  (
    orders,
    canUseReturnOrders,
    carts,
    useInventoryAllocation,
    hasOrderManageRevisionPermission,
  ) =>
    getOrdersActions(
      orders,
      canUseReturnOrders,
      carts,
      useInventoryAllocation,
      hasOrderManageRevisionPermission,
    ),
)

export const hasSelectedOrderSelector = (state) =>
  selectedOrderNumbersSelector(state).length > 0

export const orderTagQuantityMapSelector = createSelector(
  activeOrdersSelector,
  (state) => orderTagsSortedByNameSelector(state),
  (selectedOrders, orderTags) => {
    const orderTagQuantityMap = orderTags.reduce(
      (prev, tag) => ({...prev, [tag.id]: 0}),
      {},
    )

    selectedOrders.forEach((order) => {
      order.tags.forEach((tag) => {
        orderTagQuantityMap[tag.id] += tag.pending !== -1 ? 1 : 0
      })
    })

    return Object.entries(orderTagQuantityMap).reduce(
      (prev, [tagID, selectedQuantity]) => {
        const diff = selectedOrders.length - selectedQuantity
        if (diff === 0) {
          prev[tagID] = 'all'
        } else if (diff === selectedOrders.length) {
          prev[tagID] = 'none'
        } else {
          prev[tagID] = 'some'
        }

        return prev
      },
      {},
    )
  },
)

export function getOrderDateRangeParams(
  startDate,
  endDate,
  dateFilterType,
  intervalType,
  intervalAmount,
  intervalTrunc,
  timezone,
) {
  const params = {}

  if (startDate) {
    startDate = new Date(startDate)

    const startDateString = startDate.toISOString()

    if (DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_after`] = startDateString
    }
  }

  if (endDate) {
    endDate = new Date(endDate)

    const endDateString = endDate.toISOString()

    if (DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_before`] = endDateString
    }
  }

  if (intervalType) {
    intervalTrunc = ensureBoolean(intervalTrunc)

    if (DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_interval_type`] = intervalType
      params[`${dateFilterType}_interval_amount`] = intervalAmount
      params[`${dateFilterType}_interval_trunc`] = intervalTrunc
    }
  }

  if (Object.keys(params).length) {
    params.timezone = timezone
  }

  return params
}

export function summarizeOrderDateRangeParams(query) {
  let startDate
  let endDate
  let dateFilterType
  let intervalType
  let intervalAmount
  let intervalTrunc
  let timezone

  if (query.timezone) {
    timezone = query.timezone
  }

  for (const dateType of DATE_RANGE_FILTERS) {
    if (query[`${dateType}_after`]) {
      startDate = new Date(query[`${dateType}_after`])
      dateFilterType = dateType
    }
  }

  if (startDate && timezone) {
    startDate = toZonedTime(startDate, timezone)
  }

  for (const dateType of DATE_RANGE_FILTERS) {
    if (query[`${dateType}_before`]) {
      endDate = new Date(query[`${dateType}_before`])
      dateFilterType = dateType
    }
  }

  if (endDate && timezone) {
    endDate = toZonedTime(endDate, timezone)
  }

  for (const dateType of DATE_RANGE_FILTERS) {
    if (query[`${dateType}_interval_type`]) {
      intervalType = query[`${dateType}_interval_type`]
      intervalAmount = query[`${dateType}_interval_amount`]
      intervalTrunc = query[`${dateType}_interval_trunc`]
      dateFilterType = dateType
    }
  }

  return {
    startDate,
    endDate,
    dateFilterType,
    intervalType,
    intervalAmount,
    intervalTrunc,
    timezone,
  }
}

export function isUsingOldParamSelector(state) {
  const location = locationSelector(state)
  const {
    query,
    pathComponents: [resource],
  } = location

  const isOrderList = [
    ORDER_PLURAL_URI_COMPONENT,
    ORDER_SINGLE_URI_COMPONENT,
  ].includes(resource)

  if (!isOrderList) {
    return {}
  }

  return {
    tag: !!query.orderTagFilters,
    label_printed: !!query.showUnprintedOnly,
  }
}

export function orderListQuerySelector(state) {
  const cache = orderListQuerySelector.cache

  const location = locationSelector(state)
  const orderTags = orderTagsSelector(state)

  if (location === cache.location && isEqual(orderTags, cache.orderTags)) {
    return cache.result
  }

  cache.location = location
  cache.orderTags = orderTags

  const {
    query,
    pathComponents: [resource, singleStatusOrOrderNumber],
  } = location

  const orderNumber =
    !!singleStatusOrOrderNumber && resource === ORDER_SINGLE_URI_COMPONENT
      ? singleStatusOrOrderNumber
      : null

  const result = {
    mode:
      resource === ORDER_PLURAL_URI_COMPONENT ||
      (resource === ORDER_SINGLE_URI_COMPONENT && !orderNumber)
        ? LIST_MODE
        : resource === ORDER_SINGLE_URI_COMPONENT
          ? DETAIL_MODE
          : null,
    orderNumber,
    ...orderListQueryToOrderQuery(
      query,
      orderTags,
      resource,
      singleStatusOrOrderNumber,
    ),
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}
orderListQuerySelector.cache = {}

function normalizeLink(link) {
  const paths = link.split('/').filter((v) => v)

  if (paths[1] === 'order') {
    return link
  }

  return `/tag/order/${paths[1]}/`
}

export function orderListQueryToOrderQuery(
  query,
  orderTags,
  resource,
  singleStatusOrOrderNumber,
) {
  const status = [
    ...(resource === ORDER_PLURAL_URI_COMPONENT
      ? singleStatusOrOrderNumber
        ? singleStatusOrOrderNumber in ORDER_STATUS_BY_SLUG
          ? [singleStatusOrOrderNumber]
          : []
        : [DEFAULT_STATUS]
      : ensureArray(query.status).filter((s) => s in ORDER_STATUS_BY_SLUG)),
  ]

  return {
    limit: Number(query.limit) || DEFAULT_PER_PAGE,
    offset: Number(query.offset) || 0,
    status,
    tag: query.orderTagFilters
      ? ensureArray(query.orderTagFilters).reduce((prev, link) => {
          link = normalizeLink(link)
          const orderTag = Object.values(orderTags).find((t) => link === t.link)

          if (orderTag) {
            prev.push(orderTag.name)
          }

          return prev
        }, [])
      : ensureArray(query.tag),
    sales_channel: ensureArrayOfNumbers(query.cartFilters),
    supplier: ensureArrayOfNumbers(query.supplierFilters),
    shipper: ensureArrayOfNumbers(query.shipper),
    warehouse_id: ensureArrayOfNumbers(query.warehouseFilters),
    allocation_status: ensureArray(query.allocationFilters),
    label_printed:
      status.length === 0 || status.includes(SHIPPED)
        ? ensureBoolean(query.showUnprintedOnly) === true
          ? false
          : ensureTroolean(query.label_printed)
        : undefined,
    packing_list_printed: ensureTroolean(query.packing_list_printed),
    untagged: ensureBoolean(query.orderUntaggedFilter) || undefined,
    search: !isEmpty(query.searchText) ? query.searchText.trim() : undefined,
    sort: query.sort || DEFAULT_SORT,
    tag_filter_by:
      query.orderTagFilterOperator !== TAG_FILTER_OR
        ? query.orderTagFilterOperator
        : undefined,
    exclude_tags: ensureArray(query.exclude_tags),
    has_similar_open_addresses:
      (ensureBoolean(query.hasSimilarOpenAddresses) &&
        (status.length === 0 || status.includes(AWAITING_FULFILLMENT))) ||
      undefined,
    ship_to_is_bill_to: ensureTroolean(query.shipToIsBillTo),
    has_revision: ensureTroolean(query.hasRevision),
    similar_address_to:
      ((status.length === 0 || status.includes(AWAITING_FULFILLMENT)) &&
        query.similarAddressTo) ||
      undefined,
    ship_to_status: query.shipToStatus || undefined,
    ship_to_country: ensureArray(query.shipToCountry),
    batch_reference_id: ensureArray(query.batch_reference_id),
    carrier_error: ensureTroolean(query.carrier_error),
    in_batch: ensureTroolean(query.inBatch),
    ...getOrderDateRangeParams(
      query.startDate,
      query.endDate,
      query.dateFilterType,
      query.intervalType,
      query.intervalAmount,
      query.intervalTrunc,
      query.timezone,
    ),
    order_number: ensureArray(query.order_number),
    sku: ensureArray(query.sku),
  }
}

export function orderListHashBuilder(params, canUseOrders = true) {
  if (canUseOrders === false) {
    return null
  }

  let hash = `#/${ORDER_SINGLE_URI_COMPONENT}`

  return stringifyURL(hash, params)
}

export const stickyOrderListParamsSelector = createSelector(
  orderListFormSelector,
  ({sticky__limit, sticky__sort} = {}) => ({
    limit: sticky__limit !== DEFAULT_PER_PAGE ? sticky__limit : undefined,
    sort: sticky__sort !== DEFAULT_SORT ? sticky__sort : undefined,
  }),
)

export const defaultHashParamsSelector = createSelector(
  stickyOrderListParamsSelector,
  (params) => ({
    status: [DEFAULT_STATUS],
    ...params,
  }),
)

export const defaultOrderListHashSelector = createSelector(
  defaultHashParamsSelector,
  (state) => canUseOrdersSelector(state),
  orderListHashBuilder,
)

export function cleanOrderQuery(query) {
  return Object.entries(query)
    .filter(([key, value]) => {
      if (
        ![
          'limit',
          'offset',
          'status',
          'tag',
          'sales_channel',
          'shipper',
          'supplier',
          'warehouse_id',
          'allocation_status',
          'label_printed',
          'packing_list_printed',
          'untagged',
          'search',
          'sort',
          'tag_filter_by',
          'exclude_tags',
          'has_similar_open_addresses',
          'ship_to_is_bill_to',
          'has_revision',
          'similar_address_to',
          'ship_to_status',
          'ship_to_country',
          'batch_reference_id',
          'carrier_error',
          'in_batch',
          'created_after',
          'order_placed_after',
          'shipped_after',
          'dropshipped_after',
          'deliver_by_after',
          'ship_by_after',
          'created_before',
          'order_placed_before',
          'shipped_before',
          'dropshipped_before',
          'deliver_by_before',
          'ship_by_before',
          'created_interval_type',
          'created_interval_amount',
          'created_interval_trunc',
          'order_placed_interval_type',
          'order_placed_interval_amount',
          'order_placed_interval_trunc',
          'shipped_interval_type',
          'shipped_interval_amount',
          'shipped_interval_trunc',
          'dropshipped_interval_type',
          'dropshipped_interval_amount',
          'dropshipped_interval_trunc',
          'deliver_by_interval_type',
          'deliver_by_interval_amount',
          'deliver_by_interval_trunc',
          'ship_by_interval_type',
          'ship_by_interval_amount',
          'ship_by_interval_trunc',
          'timezone',
          'order_number',
          'sku',
        ].includes(key)
      ) {
        return false
      }

      if (Array.isArray(value) ? value.length === 0 : isEmptyValue(value)) {
        return false
      }

      return true
    })
    .reduce((prev, [key, value]) => {
      prev[key] = value
      return prev
    }, {})
}

export function orderListQueryParamsSelector(state) {
  const query = orderListQuerySelector(state)

  return cleanOrderQuery(query)
}

export const hasActiveFiltersSelector = createSelector(
  orderListQueryParamsSelector,
  (params) =>
    Object.keys(params).filter(
      (key) =>
        !['status', 'sort', 'limit', 'offset', 'batch_reference_id'].includes(
          key,
        ),
    ).length !== 0,
)

export const batchReferenceIDSelector = createSelector(
  orderListQuerySelector,
  (params) => params.batch_reference_id[0],
)

export function getOrderDetailURL(orderNumber, params = {}) {
  return [[ORDER_SINGLE_URI_COMPONENT, orderNumber], params]
}

export const orderStatiiSelector = createSelector(
  useDropshippingSelector,
  (useDropshipping) =>
    ORDER_STATII.filter((orderStatus) => {
      if (orderStatus.slug === 'dropshipment_requested' && !useDropshipping) {
        return false
      }

      return true
    }),
)

export function getValidDateRangeFilterTypes(status) {
  if (status.length === 1 && status[0] === AWAITING_FULFILLMENT) {
    return [DATE_ORDERED, DATE_DELIVER_BY, DATE_SHIP_BY, DATE_CREATED]
  }

  if (status.length === 1 && status[0] === DROPSHIPMENT_REQUESTED) {
    return [
      DATE_ORDERED,
      DATE_DELIVER_BY,
      DATE_SHIP_BY,
      DATE_DROPSHIPPED,
      DATE_CREATED,
    ]
  }

  return [
    DATE_ORDERED,
    DATE_DELIVER_BY,
    DATE_SHIP_BY,
    DATE_SHIPPED,
    DATE_DROPSHIPPED,
    DATE_CREATED,
  ]
}

export const validDateRangeFilterTypesSelector = createSelector(
  (state, {status} = {}) => status || orderListQuerySelector(state).status,
  (status) => getValidDateRangeFilterTypes(status),
)

export const validDateRangeFilterTypesOptionsSelector = createSelector(
  (state, {status} = {}) => validDateRangeFilterTypesSelector(state, {status}),
  (validFilterTypes) =>
    validFilterTypes.map((filterType) =>
      DATE_RANGE_FILTER_OPTIONS.find(({value}) => value === filterType),
    ),
)

export function modalValidDateRangeFilterTypesOptionsSelector(
  state,
  {modalFormSelector},
) {
  const cache = modalValidDateRangeFilterTypesOptionsSelector.cache
  const {status} = modalFormSelector(state)

  const options = validDateRangeFilterTypesOptionsSelector(state, {status})

  if (
    options.length === cache.length &&
    !options.find(({value}, index) => cache[index].value !== value)
  ) {
    return cache
  }

  modalValidDateRangeFilterTypesOptionsSelector.cache = options

  return options
}
modalValidDateRangeFilterTypesOptionsSelector.cache = []

export const allocationStatiiSelector = createSelector(
  usePartialInventoryAllocationSelector,
  (partialAllocationAllowed) =>
    ORDER_ALLOCATION_STATII.filter((allocationStatus) => {
      if (allocationStatus.slug === 'partial' && !partialAllocationAllowed) {
        return false
      }

      return true
    }),
)
